import { useMsal } from '@azure/msal-react';
import { Button, Spinner, ToggleButton } from '@fluentui/react-components';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DataService } from '../services/dataService';

type Language = 'en' | 'de';

interface ProjectData {
    projektId: number;
    projektLeiterName: string;
    projektTitel: string;
    projektRolle: string;
}

const NdaAgreement: React.FC = () => {
    const [searchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(true);
    const [projectData, setProjectData] = useState<ProjectData | null>(null);
    const { instance, accounts } = useMsal();
    const projektGuid = searchParams.get('projektGuid');
    const [isAlreadyAccepted, setIsAlreadyAccepted] = useState(false);
    const [isPm, setIsPm] = useState(false);
    const [viewOnly] = useState(searchParams.get('viewOnly') === '1');
    const [language, setLanguage] = useState<Language>('en');
    const dataService = React.useMemo(() => {
        const accountInfo = accounts[0] || null;
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accounts]);

    // NDA Content in English and German
    const ndaContent = {
        en: {
            title: 'Confidentiality Agreement for Work on the Project',
            preambleTitle: 'Preamble',
            preambleText: 'IMC University of Applied Sciences GmbH maintains active business relationships with a variety of project partners, including American companies that are subject to the laws of the United States of America. In the course of this cooperation with ALL project partners, IMC University of Applied Sciences GmbH is bound by strict confidentiality obligations, whose compliance – particularly with regard to the aforementioned American companies – is sanctioned with enormous penalties under American tort law.',
            agreementTitle: 'Confidentiality Agreement and Rights to Research Results',
            agreementParagraphs: [
                'By taking knowledge of this agreement, I commit to treating all information that becomes known to me in the course of working on all projects with strict confidentiality and to ensure that third parties do not gain access to such information under any circumstances. Confidential information includes not only written documents (e.g., technical descriptions, emails, minutes) but also information that becomes known during oral presentations, discussions, and/or negotiations, as well as the results of scientific work. The obligation of confidentiality applies regardless of whether the information in question is explicitly marked as confidential or not.',
                'This confidentiality obligation continues to apply even after the termination of my work on the project and/or after leaving the company.',
                'Violations of the above obligation can be punished under applicable law with imprisonment or fines and/or result in far-reaching employment-related consequences, as well as – through recourse by IMC University of Applied Sciences GmbH against its employees – give rise to substantial civil damages liability.',
                'I further acknowledge that all rights to the results of research work, ideas, inventions, patents, and/or copyrights, etc., regardless of the legal basis, belong to the client and no separate compensation is provided. Any statutory and contractual special provisions regarding so-called "employee inventions" remain unaffected by this.'
            ],
            footerAcknowledgment: 'I explicitly acknowledge the above provisions',
            acceptButton: 'I Accept the Project Participation and Confidentiality Agreement',
            declineButton: 'I Decline the Confidentiality Agreement and Project Participation',
            alreadyAcceptedMessage: 'You have already accepted the Project Participation and Confidentiality Agreement.',
            notProjectMemberMessage: 'You are not a project member and cannot accept the Project Participation and Confidentiality Agreement.',
            invitationText: 'You have been invited by the project manager',
        },
        de: {
            title: 'Geheimhaltungserklärung für die Mitarbeit am Projekt',
            preambleTitle: 'Präambel',
            preambleText: 'Die IMC Krems steht in aufrechten Geschäftsbeziehungen zu einer Vielzahl an Projektpartnern, unter anderem auch zu amerikanischen Unternehmen, welche dem Recht der Vereinigten Staaten von Amerika unterliegen. Im Rahmen dieser Zusammenarbeit mit ALLEN Projektpartnern unterliegt die IMC Krems strengen Geheimhaltungsverpflichtungen, deren Einhaltung – insbesondere betreffend die genannten amerikanischen Unternehmen - mit enormen Strafen entsprechend dem amerikanischen Schadenersatzrecht – sanktioniert ist.',
            agreementTitle: 'Geheimhaltungserklärung und Rechte an Forschungsergebnissen',
            agreementParagraphs: [
                'Durch Kenntnisnahme dieser Vereinbarung verpflichte ich mich, alle Informationen, die mir im Zuge der Mitarbeit an sämtlichen Projekten bekannt werden, streng vertraulich zu behandeln und dafür Sorge zu tragen, dass Dritte hiervon keinesfalls Kenntnis erlangen (können). Als vertrauliche Informationen sind neben schriftlichen Unterlagen (z.B. technische Beschreibungen, Emails, Protokolle) auch jene zu verstehen, welche z.B. während einer mündlichen Präsentation, Diskussion und/oder Verhandlungen bekannt und/oder ermittelt werden bzw. die Ergebnisse wissenschaftlicher Arbeiten. Die Verpflichtung zur Geheimhaltung gilt unabhängig davon, ob die betreffende Information ausdrücklich als vertraulich gekennzeichnet ist oder nicht.',
                'Diese Geheimhaltungsverpflichtung besteht auch nach Beendigung meiner Tätigkeit im Projekt und/oder nach dem Ausscheiden aus dem Unternehmen fort.',
                'Verstöße gegen die oben angeführte Verpflichtung können aufgrund gesetzlicher Bestimmungen mit Freiheits- oder Geldstrafen geahndet und/oder weitreichende arbeitsrechtliche Konsequenzen nach sich ziehen sowie – im Regressweg der IMC Krems gegenüber ihren MitarbeiterInnen - eine beträchtliche, zivilrechtliche Schadenersatzpflicht begründen.',
                'Ich nehme weiters zur Kenntnis, dass sämtliche Rechte an den Ergebnissen der Forschungsarbeit, Ideen, Erfindungen, Patente und/oder Copyrights etc. aus welchem Rechtstitel auch immer dem Auftraggeber zustehen und hierfür keine separate Vergütung erfolgt. Allfällige gesetzliche und vertragliche Sonderbestimmungen im Hinblick auf sog. „Diensterfindungen" bleiben hiervon unberührt.'
            ],
            footerAcknowledgment: 'Ich nehme die o.a. Bestimmungen ausdrücklich zur Kenntnis',
            acceptButton: 'Ich akzeptiere die Projekt-Teilnahme- und Geheimhaltungserklärung',
            declineButton: 'Ich lehne die Geheimhaltungserklärung und Projekt-Teilnahme ab',
            alreadyAcceptedMessage: 'Sie haben bereits die Projekt-Teilnahme- und Geheimhaltungserklärung akzeptiert.',
            notProjectMemberMessage: 'Sie sind kein Projektmitglied und können die Projekt-Teilnahme- und Geheimhaltungserklärung nicht akzeptieren.',
            invitationText: 'Sie wurden eingeladen vom Projektmanager',
        }
    };

    const content = ndaContent[language];

    useEffect(() => {
        const fetchData = async () => {
            if (projektGuid) {

                const _userInfo = await dataService?.getUserInfo();
                const _project = await dataService?.getProjektByGuid(projektGuid);

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const _isPM = _project?.projektmitarbeiter.find((m: any) => m.personalID === _userInfo?.userId);
                setIsAlreadyAccepted(_isPM?.geheimhaltungserklAkzeptiert !== null ? true : false);
                setIsPm(_isPM !== undefined);

                // TODO: Fetch project data using projektGuid and mitarbeiterID
                setTimeout(() => {
                    // Dummy data for now
                    setProjectData({
                        projektId: _project?.projektID as number,
                        projektLeiterName: _project?.projektleiterName as string,
                        projektTitel: _project?.projekttitelIntern as string,
                        projektRolle: 'Projektmitarbeiter',
                    });
                    setIsLoading(false);
                }, 1000);
            } else if (viewOnly && !projektGuid) {
                // View-only mode: no project data needed, just show the agreement
                setIsLoading(false);
            }
        };
        fetchData();
    }, [dataService, projektGuid, viewOnly]);

    const handleAccept = () => {
        dataService?.setGeheimhaltungserklaerung(projectData?.projektId as number, true);
        setIsAlreadyAccepted(true);
    };

    const handleDecline = () => {
        dataService?.setGeheimhaltungserklaerung(projectData?.projektId as number, false);
        setIsAlreadyAccepted(false);
    };

    if (!viewOnly && !projektGuid) {
        return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
                <p>Missing required parameter: projektGuid</p>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div style={{ padding: '40px', textAlign: 'center' }}>
                <Spinner label="Loading Confidentiality Agreement..." />
            </div>
        );
    }

    return (
        <div style={{
            maxWidth: '900px',
            margin: '0 auto',
            padding: '40px 20px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", sans-serif',
            position: 'relative',
        }}>
            {/* Language Switcher - Top Right */}
            <div style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                display: 'flex',
                gap: '8px',
            }}>
                <ToggleButton
                    appearance={language === 'en' ? 'primary' : 'secondary'}
                    onClick={() => setLanguage('en')}
                    checked={language === 'en'}
                >
                    English
                </ToggleButton>
                <ToggleButton
                    appearance={language === 'de' ? 'primary' : 'secondary'}
                    onClick={() => setLanguage('de')}
                    checked={language === 'de'}
                >
                    Deutsch
                </ToggleButton>
            </div>

            {/* Document Container */}
            <div style={{
                backgroundColor: '#ffffff',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                padding: '60px',
                marginTop: '20px',
            }}>
                {/* Header */}
                {(!viewOnly) && (<div style={{
                    backgroundColor: '#f5f5f5',
                    borderBottom: '2px solid #797979',
                    marginBottom: '30px',
                    paddingBottom: '20px'
                }}>

                    <p style={{
                        margin: '0 0 10px 0',
                        fontSize: '14px',
                        color: '#555'
                    }}>
                        {content.invitationText} <strong>{projectData?.projektLeiterName}</strong>
                    </p>

                </div>)}
                {(isPm || viewOnly) && (
                    <div style={{ lineHeight: '1.8', color: '#333', fontSize: '14px' }}>
                        <h1 style={{
                            fontSize: '24px',
                            fontWeight: 'bold',
                            marginTop: '0',
                            marginBottom: '20px',
                            color: '#797979',
                            textAlign: 'center',
                        }}>
                            {content.title} <br /><strong>{projectData?.projektTitel}</strong>
                        </h1>

                        <h2 style={{
                            fontSize: '18px',
                            fontWeight: 'bold',
                            marginTop: '30px',
                            marginBottom: '15px',
                            color: '#333'
                        }}>
                            {content.preambleTitle}
                        </h2>
                        <p>
                            {content.preambleText}
                        </p>

                        <h2 style={{
                            fontSize: '18px',
                            fontWeight: 'bold',
                            marginTop: '30px',
                            marginBottom: '15px',
                            color: '#333'
                        }}>
                            {content.agreementTitle}
                        </h2>
                        {content.agreementParagraphs.map((paragraph, index) => (
                            <p key={index}>
                                {paragraph}
                            </p>
                        ))}

                        {/* Footer Section */}
                        <div style={{
                            marginTop: '50px',
                            paddingTop: '30px',
                            borderTop: '1px solid #e0e0e0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontSize: '13px',
                            color: '#666'
                        }}>
                            <div>
                                {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                            </div>
                            <div>
                                {content.footerAcknowledgment}
                            </div>
                        </div>
                    </div>)}
                {isPm && isAlreadyAccepted && (
                    <div style={{ color: 'green', marginTop: '20px', textAlign: 'center' }}>
                        {content.alreadyAcceptedMessage}
                    </div>
                )}
                {isPm && !isAlreadyAccepted && (
                    <div style={{
                        marginTop: '40px',
                        display: 'flex',
                        gap: '12px',
                        justifyContent: 'center',
                        flexWrap: 'wrap'
                    }}>
                        <Button
                            appearance="primary"
                            onClick={handleAccept}
                            size="large"
                            style={{ paddingLeft: '24px', paddingRight: '24px' }}
                        >
                            {content.acceptButton}
                        </Button>
                        <Button
                            appearance="secondary"
                            onClick={handleDecline}
                            size="large"
                            style={{ paddingLeft: '24px', paddingRight: '24px' }}
                        >
                            {content.declineButton}
                        </Button>
                    </div>
                )}
                {(!viewOnly && !isPm) && (
                    <div style={{ color: 'red', marginTop: '20px', textAlign: 'center' }}>
                        {content.notProjectMemberMessage}
                    </div>
                )}
            </div>
        </div>
    );
};

export default NdaAgreement;
